import { formatAnswerSegments } from "../../utils/answerDocument";
import { StudentName } from "../../../../shared-components/StudentName";
import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  Menu,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {
  Add,
  Check,
  ChevronLeft,
  ChevronRight,
  CloseFullscreen,
  Delete,
  Edit,
  ErrorOutline,
  Fullscreen,
  MoreVert,
  Visibility,
  VisibilityOff,
} from "@mui/icons-material";
import { isHolisticMarking } from "../../../../utils/preferences";
import { DocumentWorkspace } from "./DocumentWorkspace";
import {
  ANNOTATION_PALETTE,
  SPELLING_KEY,
  buildAnnotationSegments,
  countWords,
  getSelectionOffsets,
  normaliseAnnotations,
} from "./annotationUtils";

const replaceEvidence = (answer, evidence, deleted = false) => ({
  ...answer,
  teacherFeedback: {
    ...answer.teacherFeedback,
    assessmentObjectiveFeedback:
      answer.teacherFeedback.assessmentObjectiveFeedback.map((ao) => ({
        ...ao,
        assessmentObjectiveEvidence: ao.assessmentObjectiveEvidence.some(
          (item) => item.id === evidence.id,
        )
          ? deleted
            ? ao.assessmentObjectiveEvidence.filter(
                (item) => item.id !== evidence.id,
              )
            : ao.assessmentObjectiveEvidence.map((item) =>
                item.id === evidence.id ? evidence : item,
              )
          : ao.assessmentObjectiveEvidence,
      })),
  },
});

const MIN_WORKSPACE_HEIGHT = 360;
const MAX_WORKSPACE_HEIGHT = 720;
const DOCUMENT_FOOTER_HEIGHT = 64;

const replaceSpelling = (answer, spelling, deleted = false) => ({
  ...answer,
  teacherFeedback: {
    ...answer.teacherFeedback,
    spellingAndGrammar: deleted
      ? answer.teacherFeedback.spellingAndGrammar.filter(
          (item) => item.id !== spelling.id,
        )
      : answer.teacherFeedback.spellingAndGrammar.map((item) =>
          item.id === spelling.id ? spelling : item,
        ),
  },
});

const AnnotationForm = ({
  type,
  annotation,
  selection,
  onCancel,
  onSave,
  formRef,
  accentColour,
}) => {
  const [reasoning, setReasoning] = useState(annotation?.reasoning || "");
  const [secondary, setSecondary] = useState(
    type === "spelling"
      ? annotation?.mistake?.correctSpelling || ""
      : annotation?.feedback || "",
  );
  const valid = reasoning.trim() && secondary.trim();
  return (
    <Stack
      ref={formRef}
      spacing={2}
      sx={{
        p: 2,
        border: annotation ? 0 : "2px solid",
        borderColor: annotation ? "transparent" : accentColour?.main,
        borderRadius: 2,
        bgcolor: annotation ? "transparent" : accentColour?.light,
      }}
    >
      <Typography variant="subtitle1">
        {annotation ? "Edit feedback" : "Add feedback"}
      </Typography>
      {!annotation && (
        <Typography variant="body2" color="text.secondary">
          Selected text: “{selection?.text}”
        </Typography>
      )}
      <TextField
        label={type === "spelling" ? "Corrected text" : "Next level"}
        multiline
        minRows={2}
        value={secondary}
        onChange={(event) => setSecondary(event.target.value)}
      />
      <TextField
        label="Explanation"
        multiline
        minRows={3}
        value={reasoning}
        onChange={(event) => setReasoning(event.target.value)}
      />
      <Stack direction="row" justifyContent="flex-end" spacing={1}>
        <Button onClick={onCancel}>Cancel</Button>
        <Button
          variant="contained"
          disabled={!valid}
          onClick={() => onSave({ reasoning, secondary })}
        >
          {annotation ? "Save" : "Add"}
        </Button>
      </Stack>
    </Stack>
  );
};

const FeedbackCard = ({
  annotation,
  active,
  colour,
  canAuthor,
  onSelect,
  onEdit,
  onDelete,
  cardRef,
}) => {
  const [anchor, setAnchor] = useState(null);
  return (
    <Box
      ref={cardRef}
      component="article"
      tabIndex={0}
      aria-label={`Annotation ${annotation.number}`}
      onClick={onSelect}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") onSelect();
      }}
      sx={{
        p: 2,
        border: "1px solid",
        borderColor: active ? colour.main : "divider",
        borderRadius: 2,
        bgcolor: active ? colour.light : "background.paper",
        cursor: "pointer",
        outline: "none",
        "&:focus-visible": { boxShadow: `0 0 0 3px ${colour.light}` },
      }}
    >
      <Stack direction="row" spacing={1.5} alignItems="flex-start">
        <Box
          sx={{
            bgcolor: colour.main,
            color: "common.white",
            borderRadius: "50%",
            width: 24,
            height: 24,
            display: "grid",
            placeItems: "center",
            fontWeight: 700,
            flexShrink: 0,
          }}
        >
          {annotation.number}
        </Box>
        <Stack spacing={1.5} sx={{ flex: 1, minWidth: 0 }}>
          {annotation.type === "spelling" && (
            <Typography variant="body2">
              <strong>Correction:</strong> {annotation.mistake.correctSpelling}
            </Typography>
          )}
          <Typography variant="body2">{annotation.reasoning}</Typography>
          {annotation.type !== "spelling" && annotation.feedback && (
            <Box>
              <Typography variant="caption" fontWeight={700}>
                Next level
              </Typography>
              <Typography variant="body2">{annotation.feedback}</Typography>
            </Box>
          )}
        </Stack>
        {canAuthor && (
          <>
            <IconButton
              size="small"
              aria-label={`More actions for annotation ${annotation.number}`}
              onClick={(event) => {
                event.stopPropagation();
                setAnchor(event.currentTarget);
              }}
            >
              <MoreVert />
            </IconButton>
            <Menu
              anchorEl={anchor}
              open={Boolean(anchor)}
              onClose={() => setAnchor(null)}
            >
              <MenuItem
                onClick={() => {
                  setAnchor(null);
                  onEdit();
                }}
              >
                <Edit fontSize="small" sx={{ mr: 1 }} /> Edit feedback
              </MenuItem>
              <MenuItem
                onClick={() => {
                  setAnchor(null);
                  onDelete();
                }}
              >
                <Delete fontSize="small" sx={{ mr: 1 }} /> Delete feedback
              </MenuItem>
            </Menu>
          </>
        )}
      </Stack>
    </Box>
  );
};

export const AnswerReview = ({
  ApiClient,
  studentAnswer,
  setStudentAnswer,
  onMarkChange,
  assignmentPreferences,
  mode = "teacher-review",
  onOpenExtraction,
}) => {
  const theme = useTheme();
  const compact = useMediaQuery(theme.breakpoints.down("md"));
  const feedback =
    mode === "moderator-review"
      ? studentAnswer?.moderatorFeedback || studentAnswer?.automatedFeedback
      : studentAnswer?.teacherFeedback;
  const aoFeedback = feedback?.assessmentObjectiveFeedback || [];
  const options = useMemo(
    () => [
      ...aoFeedback.map((ao) => {
        const configuredObjective = (
          studentAnswer?.question?.markScheme?.objectives || []
        ).find(
          (objective) =>
            objective.assessmentObjective.id === ao.assessmentObjective.id,
        );
        return {
          key: ao.id,
          label: ao.assessmentObjective.identifier,
          description:
            ao.assessmentObjective.focusArea ||
            configuredObjective?.assessmentObjective?.focusArea ||
            "Assessment feedback",
          ao: {
            ...ao,
            availableMarks:
              ao.availableMarks ?? configuredObjective?.marks ?? 0,
          },
        };
      }),
      {
        key: SPELLING_KEY,
        label: "Spelling & grammar",
        description: "Spelling and grammar feedback",
      },
    ],
    [aoFeedback, studentAnswer?.question?.markScheme?.objectives],
  );
  const [selectedKey, setSelectedKey] = useState(
    options[0]?.key || SPELLING_KEY,
  );
  const [selectedId, setSelectedId] = useState(null);
  const [selection, setSelection] = useState(null);
  const [adding, setAdding] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [showAnnotations, setShowAnnotations] = useState(true);
  const [focused, setFocused] = useState(false);
  const [saveState, setSaveState] = useState(null);
  const [workspaceHeight, setWorkspaceHeight] = useState(MIN_WORKSPACE_HEIGHT);
  const documentRef = useRef(null);
  const answerContentRef = useRef(null);
  const addFormRef = useRef(null);
  const pageScrollPositionRef = useRef(0);
  const highlightRefs = useRef({});
  const cardRefs = useRef({});

  useEffect(() => {
    if (!options.some((option) => option.key === selectedKey)) {
      setSelectedKey(options[0]?.key || SPELLING_KEY);
    }
  }, [options, selectedKey]);

  useEffect(() => {
    setSelectedId(null);
    setSelection(null);
    setAdding(false);
    setEditingId(null);
  }, [selectedKey]);

  useEffect(() => {
    if (!focused) return undefined;
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previous;
    };
  }, [focused]);

  const toggleFocused = () => {
    if (!focused) {
      pageScrollPositionRef.current = window.scrollY;
      setFocused(true);
      return;
    }
    setFocused(false);
    window.requestAnimationFrame(() => {
      window.scrollTo({ top: pageScrollPositionRef.current });
    });
  };

  const selectedOption = options.find((option) => option.key === selectedKey);
  const selectedIndex = Math.max(
    0,
    options.findIndex((option) => option.key === selectedKey),
  );
  const type = selectedKey === SPELLING_KEY ? "spelling" : "assessment";
  const rawItems =
    type === "spelling"
      ? feedback?.spellingAndGrammar || []
      : selectedOption?.ao?.assessmentObjectiveEvidence || [];
  const annotations = normaliseAnnotations(
    rawItems,
    (studentAnswer.answer || "").length,
    type,
  );
  const colour = ANNOTATION_PALETTE[selectedIndex % ANNOTATION_PALETTE.length];
  const canAuthor = mode === "teacher-review";
  const canEditMark = canAuthor && !isHolisticMarking(assignmentPreferences);
  const canDraftMark = mode === "moderator-review";
  const segments = useMemo(
    () =>
      formatAnswerSegments(
        buildAnnotationSegments(
          studentAnswer.answer || "",
          showAnnotations ? annotations : [],
        ),
        studentAnswer.answerDocument,
        studentAnswer.answer || "",
      ),
    [
      annotations,
      showAnnotations,
      studentAnswer.answer,
      studentAnswer.answerDocument,
    ],
  );

  const measureWorkspace = useCallback(() => {
    const answerHeight = answerContentRef.current?.scrollHeight || 0;
    setWorkspaceHeight(
      Math.min(
        MAX_WORKSPACE_HEIGHT,
        Math.max(MIN_WORKSPACE_HEIGHT, answerHeight + DOCUMENT_FOOTER_HEIGHT),
      ),
    );
  }, []);

  useLayoutEffect(() => {
    measureWorkspace();
    if (!answerContentRef.current || typeof ResizeObserver === "undefined") {
      return undefined;
    }
    const observer = new ResizeObserver(measureWorkspace);
    observer.observe(answerContentRef.current);
    return () => observer.disconnect();
  }, [measureWorkspace, segments]);

  useEffect(() => {
    if (adding && sidebarOpen) {
      addFormRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }
  }, [adding, sidebarOpen]);

  const selectAnnotation = (annotation, source) => {
    setSelectedId(annotation.id);
    setSidebarOpen(true);
    const target =
      source === "document"
        ? cardRefs.current[annotation.id]
        : highlightRefs.current[annotation.id];
    target?.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  const handleSelection = () => {
    if (!canAuthor) return;
    const offsets = getSelectionOffsets(
      documentRef.current,
      window.getSelection(),
      studentAnswer.answer || "",
    );
    setSelection(offsets);
    if (!offsets) setAdding(false);
  };

  const saveNewAnnotation = async ({ reasoning, secondary }) => {
    if (!selection) return;
    setSaveState("saving");
    try {
      if (type === "spelling") {
        const response =
          await ApiClient.assignment.createSpellingAndGrammarFeedback({
            feedback: feedback.id,
            example: selection.text,
            correctSpelling: secondary,
            reasoning,
            startIndex: selection.startIndex,
            endIndex: selection.endIndex,
          });
        if (!response.success) throw new Error(response.message);
        setStudentAnswer((answer) => ({
          ...answer,
          teacherFeedback: {
            ...answer.teacherFeedback,
            spellingAndGrammar: [
              ...(answer.teacherFeedback.spellingAndGrammar || []),
              response.spellingFeedback,
            ],
          },
        }));
        setSelectedId(response.spellingFeedback.id);
      } else {
        const response = await ApiClient.assignment.createFeedbackEvidence({
          assessmentObjectiveFeedback: selectedOption.ao.id,
          example: selection.text,
          reasoning,
          howToImprove: secondary,
          startIndex: selection.startIndex,
          endIndex: selection.endIndex,
        });
        if (!response.success) throw new Error(response.message);
        setStudentAnswer((answer) => ({
          ...answer,
          teacherFeedback: {
            ...answer.teacherFeedback,
            assessmentObjectiveFeedback:
              answer.teacherFeedback.assessmentObjectiveFeedback.map((ao) =>
                ao.id === selectedOption.ao.id
                  ? {
                      ...ao,
                      assessmentObjectiveEvidence: [
                        ...ao.assessmentObjectiveEvidence,
                        response.evidence,
                      ],
                    }
                  : ao,
              ),
          },
        }));
        setSelectedId(response.evidence.id);
      }
      setAdding(false);
      setSelection(null);
      window.getSelection()?.removeAllRanges();
      setSaveState("saved");
    } catch (error) {
      console.error("Unable to add feedback", error);
      setSaveState("error");
    }
  };

  const saveExisting = async (annotation, values) => {
    setSaveState("saving");
    try {
      if (annotation.type === "spelling") {
        const response =
          await ApiClient.assignment.updateSpellingAndGrammarFeedback({
            spellingFeedback: annotation.id,
            correctSpelling: values.secondary,
            reasoning: values.reasoning,
          });
        if (!response.success) throw new Error(response.message);
        setStudentAnswer((answer) =>
          replaceSpelling(answer, response.spellingFeedback),
        );
      } else {
        const response = await ApiClient.assignment.updateFeedbackEvidence({
          evidence: annotation.id,
          reasoning: values.reasoning,
          howToImprove: values.secondary,
          isDelete: false,
        });
        if (!response.success) throw new Error(response.message);
        setStudentAnswer((answer) =>
          replaceEvidence(answer, response.evidence),
        );
      }
      setEditingId(null);
      setSaveState("saved");
    } catch (error) {
      console.error("Unable to update feedback", error);
      setSaveState("error");
    }
  };

  const deleteAnnotation = async () => {
    const annotation = deleteTarget;
    setDeleteTarget(null);
    setSaveState("saving");
    try {
      if (annotation.type === "spelling") {
        const response =
          await ApiClient.assignment.updateSpellingAndGrammarFeedback({
            spellingFeedback: annotation.id,
            correctSpelling: annotation.mistake.correctSpelling,
            reasoning: annotation.reasoning,
            isDelete: true,
          });
        if (!response.success) throw new Error(response.message);
        setStudentAnswer((answer) => replaceSpelling(answer, annotation, true));
      } else {
        const response = await ApiClient.assignment.updateFeedbackEvidence({
          evidence: annotation.id,
          reasoning: "",
          howToImprove: "",
          isDelete: true,
        });
        if (!response.success) throw new Error(response.message);
        setStudentAnswer((answer) => replaceEvidence(answer, annotation, true));
      }
      setSelectedId(null);
      setSaveState("saved");
    } catch (error) {
      console.error("Unable to delete feedback", error);
      setSaveState("error");
    }
  };

  const handleMarkChange = async (event) => {
    const ao = selectedOption?.ao;
    if (!ao) return;
    const previous = ao.mark;
    const max = ao.availableMarks ?? 0;
    const value = Math.max(0, Math.min(Number(event.target.value) || 0, max));
    const aggregateMarkKey =
      mode === "moderator-review" ? ao.assessmentObjective.id : ao.id;
    onMarkChange?.(aggregateMarkKey, value);
    setStudentAnswer((answer) => {
      const key =
        mode === "moderator-review" ? "moderatorFeedback" : "teacherFeedback";
      const source =
        answer[key] ||
        (mode === "moderator-review" ? answer.automatedFeedback : null);
      if (!source) return answer;
      return {
        ...answer,
        [key]: {
          ...source,
          assessmentObjectiveFeedback: source.assessmentObjectiveFeedback.map(
            (item) => (item.id === ao.id ? { ...item, mark: value } : item),
          ),
        },
      };
    });
    if (!canEditMark) return;
    setSaveState("saving");
    try {
      const response =
        await ApiClient.assignment.updateAssessmentObjectiveOFeedbackMark({
          assessmentObjectiveFeedback: ao.id,
          mark: value,
        });
      if (!response.success) throw new Error(response.message);
      setSaveState("saved");
    } catch (error) {
      console.error("Unable to save mark", error);
      onMarkChange?.(aggregateMarkKey, previous);
      setStudentAnswer((answer) => ({
        ...answer,
        teacherFeedback: {
          ...answer.teacherFeedback,
          assessmentObjectiveFeedback:
            answer.teacherFeedback.assessmentObjectiveFeedback.map((item) =>
              item.id === ao.id ? { ...item, mark: previous } : item,
            ),
        },
      }));
      setSaveState("error");
    }
  };

  const sidebar = (
    <Stack sx={{ height: "100%", minHeight: 0 }}>
      <Stack direction="row" alignItems="center" sx={{ p: 2 }} spacing={1}>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ flex: 1 }}>
          <Typography variant="h6">
            {type === "spelling"
              ? "Spelling & grammar"
              : `Feedback for ${selectedOption?.label}`}
          </Typography>
          <Chip size="small" label={`${annotations.length} total`} />
        </Stack>
        {!compact && (
          <IconButton
            size="small"
            aria-label="Collapse feedback"
            onClick={() => setSidebarOpen(false)}
          >
            <ChevronRight />
          </IconButton>
        )}
      </Stack>
      <Divider />
      <Stack spacing={1.5} sx={{ p: 2, overflowY: "auto", flex: 1 }}>
        {annotations.length === 0 && !adding && (
          <Box sx={{ py: 4, textAlign: "center" }}>
            <Typography color="text.secondary">
              No feedback has been added for this{" "}
              {type === "spelling" ? "area" : "assessment objective"}.
            </Typography>
          </Box>
        )}
        {annotations.map((annotation) =>
          editingId === annotation.id ? (
            <AnnotationForm
              key={annotation.id}
              type={type}
              annotation={annotation}
              onCancel={() => setEditingId(null)}
              onSave={(values) => saveExisting(annotation, values)}
            />
          ) : (
            <FeedbackCard
              key={annotation.id}
              annotation={annotation}
              active={annotation.id === selectedId}
              colour={colour}
              canAuthor={canAuthor}
              onSelect={() => selectAnnotation(annotation, "card")}
              onEdit={() => setEditingId(annotation.id)}
              onDelete={() => setDeleteTarget(annotation)}
              cardRef={(element) => {
                cardRefs.current[annotation.id] = element;
              }}
            />
          ),
        )}
        {adding && (
          <AnnotationForm
            type={type}
            selection={selection}
            formRef={addFormRef}
            accentColour={colour}
            onCancel={() => setAdding(false)}
            onSave={saveNewAnnotation}
          />
        )}
      </Stack>
      {canAuthor && (
        <Box sx={{ p: 2, borderTop: "1px solid", borderColor: "divider" }}>
          <Button
            startIcon={<Add />}
            variant="outlined"
            fullWidth
            onClick={() => {
              if (selection) setAdding(true);
              else documentRef.current?.focus();
            }}
          >
            {selection ? "Add feedback" : "Select text to add feedback"}
          </Button>
        </Box>
      )}
    </Stack>
  );

  return (
    <Box
      sx={{
        width: "100%",
        maxWidth: "100%",
        minWidth: 0,
        ...(focused
          ? {
              position: "fixed",
              inset: 0,
              zIndex: (currentTheme) => currentTheme.zIndex.modal + 1,
              bgcolor: "background.default",
              p: { xs: 0, md: 2 },
              overflow: "auto",
            }
          : {}),
      }}
    >
      <Stack
        direction={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", sm: "center" }}
        spacing={1}
        sx={{ mb: 1.5 }}
      >
        <Box>
          <Typography variant="h4">
            {mode === "student-review" ? "Review your answer" : "Review answer"}
          </Typography>
          <Typography color="text.secondary">
            Review assessment objectives and feedback against the answer.
          </Typography>
        </Box>
        <Button
          startIcon={focused ? <CloseFullscreen /> : <Fullscreen />}
          onClick={toggleFocused}
        >
          {focused ? "Collapse review" : "Expand review"}
        </Button>
      </Stack>
      <DocumentWorkspace focused={focused}>
        <Box sx={{ p: 2, borderBottom: "1px solid", borderColor: "divider" }}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            spacing={1}
            sx={{ mb: 2 }}
          >
            <Typography variant="h6">
              {mode === "student-review" ? (
                "Your answer"
              ) : studentAnswer?.student?.identifier ? (
                <>
                  <StudentName student={studentAnswer.student} />
                  's Answer
                </>
              ) : (
                "Student's Answer"
              )}
            </Typography>
            {mode === "teacher-review" && saveState && (
              <Stack direction="row" spacing={0.5} alignItems="center">
                {saveState === "saving" && <CircularProgress size={14} />}
                {saveState === "saved" && (
                  <Check fontSize="small" color="success" />
                )}
                {saveState === "error" && (
                  <ErrorOutline fontSize="small" color="error" />
                )}
                <Typography
                  variant="caption"
                  color={saveState === "error" ? "error" : "text.secondary"}
                >
                  {saveState === "saving"
                    ? "Saving…"
                    : saveState === "saved"
                      ? "Saved"
                      : "Unable to save — Retry"}
                </Typography>
              </Stack>
            )}
          </Stack>
          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={3}
            alignItems={{ xs: "stretch", md: "flex-end" }}
            justifyContent="space-between"
          >
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={2}
              alignItems={{ xs: "stretch", sm: "center" }}
              sx={{
                flex: "1 1 auto",
                minWidth: 0,
                maxWidth: { md: 700 },
              }}
            >
              <FormControl
                size="small"
                sx={{
                  width: { xs: "100%", sm: "auto" },
                  flex: { sm: "1 1 420px" },
                  minWidth: 0,
                }}
              >
                <InputLabel id="assessment-objective-label">
                  Assessment objective
                </InputLabel>
                <Select
                  labelId="assessment-objective-label"
                  label="Assessment objective"
                  value={selectedKey}
                  onChange={(event) => setSelectedKey(event.target.value)}
                  aria-label="Assessment objective"
                  renderValue={(key) => {
                    const option = options.find((item) => item.key === key);
                    return (
                      <Typography component="span" noWrap display="block">
                        {option?.label} — {option?.description}
                      </Typography>
                    );
                  }}
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        maxWidth: "min(620px, calc(100vw - 32px))",
                      },
                    },
                  }}
                >
                  {options.map((option) => (
                    <MenuItem
                      key={option.key}
                      value={option.key}
                      sx={{
                        whiteSpace: "normal",
                        overflowWrap: "anywhere",
                        py: 1.25,
                        lineHeight: 1.4,
                      }}
                    >
                      {option.label} — {option.description}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="center"
                sx={{ minHeight: 40, flexShrink: 0 }}
              >
                <IconButton
                  aria-label="Previous assessment objective"
                  disabled={selectedIndex === 0}
                  onClick={() => setSelectedKey(options[selectedIndex - 1].key)}
                >
                  <ChevronLeft />
                </IconButton>
                <Typography sx={{ minWidth: 64, textAlign: "center" }}>
                  {selectedIndex + 1} of {options.length}
                </Typography>
                <IconButton
                  aria-label="Next assessment objective"
                  disabled={selectedIndex === options.length - 1}
                  onClick={() => setSelectedKey(options[selectedIndex + 1].key)}
                >
                  <ChevronRight />
                </IconButton>
              </Stack>
            </Stack>
            {type === "assessment" && (
              <Box
                sx={{
                  px: 2,
                  py: 1,
                  minWidth: { md: 190 },
                  ml: { md: "auto" },
                  flexShrink: 0,
                  borderRadius: 1.5,
                  bgcolor: "action.hover",
                }}
              >
                <Typography variant="caption" fontWeight={700}>
                  {selectedOption?.label} mark
                </Typography>
                <Stack direction="row" alignItems="center" spacing={1}>
                  {canEditMark || canDraftMark ? (
                    <TextField
                      type="number"
                      size="small"
                      value={selectedOption?.ao?.mark ?? ""}
                      onChange={handleMarkChange}
                      inputProps={{
                        min: 0,
                        max: selectedOption?.ao?.availableMarks ?? 0,
                        "aria-label": `Mark for ${selectedOption?.label}`,
                      }}
                      sx={{ width: 70, bgcolor: "background.paper" }}
                    />
                  ) : isHolisticMarking(assignmentPreferences) &&
                    mode === "teacher-review" ? (
                    <Chip label="Holistic marking" />
                  ) : (
                    <Typography variant="h6" fontWeight={700}>
                      {selectedOption?.ao?.mark ?? "—"}
                    </Typography>
                  )}
                  <Typography fontWeight={600}>
                    / {selectedOption?.ao?.availableMarks ?? 0}
                  </Typography>
                </Stack>
              </Box>
            )}
          </Stack>
        </Box>
        {studentAnswer.handwrittenAnswerFile?.extractionQualityReview
          ?.reviewSuggested && (
          <Alert
            severity={
              studentAnswer.handwrittenAnswerFile.extractionQualityReview
                .qualityLevel === "low"
                ? "error"
                : "warning"
            }
            action={
              mode === "teacher-review" &&
              studentAnswer.handwrittenAnswerFile.preSignedUrl ? (
                <Button color="inherit" size="small" onClick={onOpenExtraction}>
                  Review extraction
                </Button>
              ) : null
            }
            sx={{ borderRadius: 0 }}
          >
            {
              studentAnswer.handwrittenAnswerFile.extractionQualityReview
                .qualityReason
            }
          </Alert>
        )}
        <Box
          sx={{
            display: "grid",
            width: "100%",
            maxWidth: "100%",
            minWidth: 0,
            gridTemplateColumns:
              sidebarOpen && !compact
                ? "minmax(0, 7fr) minmax(280px, 3fr)"
                : "1fr",
            height: focused ? "calc(100vh - 230px)" : workspaceHeight,
          }}
        >
          <Stack
            sx={{
              height: "100%",
              minWidth: 0,
              minHeight: 0,
              borderRight: sidebarOpen && !compact ? "1px solid" : 0,
              borderColor: "divider",
            }}
          >
            {!sidebarOpen && !compact && (
              <Button
                sx={{ alignSelf: "flex-end", m: 1 }}
                onClick={() => setSidebarOpen(true)}
              >
                Show feedback
              </Button>
            )}
            <Box
              ref={documentRef}
              tabIndex={0}
              onMouseUp={handleSelection}
              sx={{
                overflowY: "auto",
                flex: 1,
                minHeight: 0,
              }}
            >
              <Box
                ref={answerContentRef}
                sx={{
                  px: { xs: 3, sm: 6, lg: 9 },
                  py: { xs: 4, md: 6 },
                  fontSize: "1.05rem",
                  lineHeight: 1.9,
                  whiteSpace: "pre-wrap",
                  userSelect: "text",
                }}
              >
                {segments.map((segment) => {
                  const primary = segment.annotations[0];
                  const active = segment.annotations.some(
                    (annotation) => annotation.id === selectedId,
                  );
                  const endingAnnotations = segment.annotations.filter(
                    (annotation) =>
                      annotation.locations.some(
                        (location) => location.endIndex === segment.end,
                      ),
                  );
                  return (
                    <React.Fragment key={`${segment.start}-${segment.end}`}>
                      <Box
                        component="span"
                        data-answer-segment="true"
                        ref={(element) => {
                          segment.annotations.forEach((annotation) => {
                            if (!highlightRefs.current[annotation.id]) {
                              highlightRefs.current[annotation.id] = element;
                            }
                          });
                        }}
                        onClick={() => {
                          if (primary) selectAnnotation(primary, "document");
                        }}
                        aria-label={
                          primary ? `Annotation ${primary.number}` : undefined
                        }
                        sx={
                          primary
                            ? {
                                bgcolor: colour.light,
                                boxShadow: active
                                  ? `inset 0 -3px 0 ${colour.main}`
                                  : `inset 0 -1px 0 ${colour.main}`,
                                cursor: "pointer",
                              }
                            : {}
                        }
                      >
                        <Box
                          component="span"
                          sx={{
                            fontWeight: segment.marks?.some(
                              (mark) => mark.type === "bold",
                            )
                              ? "bold"
                              : undefined,
                            fontStyle: segment.marks?.some(
                              (mark) => mark.type === "italic",
                            )
                              ? "italic"
                              : undefined,
                            textDecoration: segment.marks?.some(
                              (mark) => mark.type === "underline",
                            )
                              ? "underline"
                              : undefined,
                            ...(segment.list
                              ? {
                                  pl: segment.list.depth * 2,
                                  "&::before": {
                                    content: `"${segment.list.marker} "`,
                                  },
                                }
                              : {}),
                          }}
                        >
                          {segment.text}
                        </Box>
                      </Box>
                      {endingAnnotations.map((annotation) => (
                        <Box
                          key={`${annotation.id}-${segment.end}`}
                          component="button"
                          type="button"
                          aria-label={`Annotation ${annotation.number}`}
                          onClick={() =>
                            selectAnnotation(annotation, "document")
                          }
                          sx={{
                            border: 0,
                            ml: 0.5,
                            p: 0,
                            width: 21,
                            height: 21,
                            borderRadius: "50%",
                            bgcolor: colour.main,
                            color: "common.white",
                            fontSize: 12,
                            fontWeight: 700,
                            cursor: "pointer",
                            verticalAlign: "text-top",
                          }}
                        >
                          {annotation.number}
                        </Box>
                      ))}
                    </React.Fragment>
                  );
                })}
              </Box>
            </Box>
            {selection && canAuthor && !adding && (
              <Button
                startIcon={<Add />}
                variant="contained"
                sx={{ alignSelf: "center", mb: 2 }}
                onClick={() => {
                  setAdding(true);
                  setSidebarOpen(true);
                }}
              >
                Add feedback
              </Button>
            )}
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={1}
              alignItems={{ xs: "stretch", sm: "center" }}
              sx={{
                px: 2,
                py: 1.5,
                borderTop: "1px solid",
                borderColor: "divider",
              }}
            >
              <Button
                size="small"
                startIcon={showAnnotations ? <VisibilityOff /> : <Visibility />}
                onClick={() => setShowAnnotations((value) => !value)}
              >
                {showAnnotations ? "Hide annotations" : "Show annotations"}
              </Button>
              <Stack direction="row" spacing={0.5} sx={{ flex: 1 }}>
                {annotations.slice(0, 8).map((annotation) => (
                  <IconButton
                    key={annotation.id}
                    size="small"
                    aria-label={`Go to annotation ${annotation.number}`}
                    onClick={() => selectAnnotation(annotation, "card")}
                    sx={{
                      bgcolor:
                        annotation.id === selectedId
                          ? colour.main
                          : colour.light,
                      color:
                        annotation.id === selectedId
                          ? "common.white"
                          : "text.primary",
                    }}
                  >
                    <Typography variant="caption">
                      {annotation.number}
                    </Typography>
                  </IconButton>
                ))}
              </Stack>
              <Typography variant="body2" color="text.secondary">
                {countWords(studentAnswer.answer || "")} words
              </Typography>
            </Stack>
          </Stack>
          {sidebarOpen && !compact && (
            <Box sx={{ minWidth: 0, minHeight: 0, height: "100%" }}>
              {sidebar}
            </Box>
          )}
        </Box>
        {compact && (
          <Box sx={{ borderTop: "1px solid", borderColor: "divider" }}>
            <Button fullWidth onClick={() => setSidebarOpen((value) => !value)}>
              {sidebarOpen ? "Hide feedback" : "Show feedback"}
            </Button>
            {sidebarOpen && (
              <Box sx={{ height: workspaceHeight, minHeight: 0 }}>
                {sidebar}
              </Box>
            )}
          </Box>
        )}
      </DocumentWorkspace>
      <Dialog
        open={Boolean(deleteTarget)}
        onClose={() => setDeleteTarget(null)}
      >
        <DialogTitle>Delete feedback?</DialogTitle>
        <DialogContent>
          This removes the feedback and its highlight from the answer.
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={deleteAnnotation}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

// The public sample shares the live review UI without mounting its API adapter.
export const ReadOnlyAnswerReview = ({ studentAnswer }) => (
  <AnswerReview studentAnswer={studentAnswer} mode="read-only" />
);
